import React, { useRef } from "react";

import {
  ConsultationQuestions,
  ModuleHeader,
  ModuleProvider,
  ModuleSection,
  ModuleQuizForm,
  StepsToAssess,
  WorkplaceAssessment,
  SmartObjectives,
  ActionPlan,
  ModuleFooter,
  useModuleState,
} from "../../components";
import {
  useAppContextMethods,
  useGlobalUserMethods,
  useGlobalUserState,
} from "~/state";

import ladyAtWork from "../../assets/gfx/04_work.jpg";
import { upsertHealthyWorkplace } from "~/util";

interface ModuleProps {}

export const Module4: React.FC<ModuleProps> = ({}) => {
  const { finish } = useGlobalUserMethods();
  const user = useGlobalUserState();
  const [state, setState, { handleSectionChange }] = useModuleState();

  const { scrollToTop } = useAppContextMethods();

  const headerRef = useRef<HTMLElement>(null);
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const section4Ref = useRef(null);
  const section5Ref = useRef(null);
  const section6Ref = useRef(null);
  const section7Ref = useRef(null);
  const section8Ref = useRef(null);
  const section9Ref = useRef(null);

  const QUIZ_4 = [
    {
      question: "A workplace health and wellbeing program involves:",
      options: [
        "Workplace assessment, staff consultation, developing an action plan and evaluation",
        "Workplace assessment, staff consultation and developing an action plan",
        "Consultation, workplace assessment and pre-evaluation",
      ],
      answer: [0],
      related: section1Ref.current,
    },
    {
      question: "The best way to consult with staff is:",
      options: [
        "Through a range of consultation methods (e.g. surveys, focus groups, face-to-face conversations etc.) to include as many staff as possible.",
        "Only through a paper-based survey distributed and collected at a compulsory meeting.",
        "Only through recruiting workplace health champions to help collect staff thoughts.",
        "Only through face-to-face conversations and focus groups.",
      ],
      answer: [0],
      related: section4Ref.current,
    },
    {
      question:
        "It’s important to plan for evaluation at the beginning because (you can select more than one answer!):",
      options: [
        "It determines baseline data which you can use to identify if your program was successful once strategies are complete",
        "It allows you identify what strategies were unsuccessful",
        "It allows you to identify barriers that are impacting on the success of the program",
        "It allows you to demonstrate program success to management once strategies are complete, increasing the chance of ongoing support",
      ],
      answer: [0, 1, 2, 3],
      related: section8Ref.current,
    },
    {
      question:
        "It’s important to develop SMART objectives. What does SMART stand for?",
      options: [
        "Simple, Measurable, Accurate, Realistic, Time bound",
        "Specific, Maintainable, Achievable, Relevant, Timely",
        "Specific, Measurable, Achievable, Relevant, Time bound",
        "Safe, Manageable, Achievable, Realistic, Tangible",
      ],
      answer: [2],
      related: section6Ref.current,
    },
    {
      question:
        "Why is it important to use several methods of consultation? You can select more than one answer!",
      options: [
        "You have a better chance of engaging with hard to reach workers",
        "Some methods may not be appropriate for certain groups of workers",
        "To give workers an opportunity to respond more than once in order to get what they want",
        "You’ll achieve a higher response rate overall",
        "You’re more likely to obtain answers that represent your entire workforce",
      ],
      answer: [0, 1, 3, 4],
      related: section4Ref.current,
    },
  ];

  return (
    <ModuleProvider
      onSectionChange={(section) => {
        console.log(section);
        setState((prev) => ({
          ...prev,
          currentSectionTitle: section!.sectionTitle,
          currentSection: section!.sectionNumber,
          currentSectionData: section!.sectionData,
        }));
      }}
    >
      <ModuleHeader currentPage={4} totalPages={4} ref={headerRef} />

      <ModuleSection
        id="introduction"
        sectionTitle="Introduction"
        sectionNumber={1}
        ref={section1Ref}
      >
        <div className="page-header block-blueberry">
          <h1>Planning your program</h1>
        </div>
      </ModuleSection>

      <ModuleSection
        id="workplace-assessment"
        sectionTitle="Workplace assessment and consultation"
        sectionNumber={2}
        className="mb-4"
        ref={section2Ref}
      >
        <WorkplaceAssessment />
      </ModuleSection>

      <ModuleSection
        id="steps-to-assessing"
        sectionTitle="Steps to assessing your workplace"
        sectionNumber={3}
        className="bg-primary section-arrows"
        fluid
        ref={section3Ref}
      >
        <h2 className="d-block h1 text-uppercase text-white text-center py-4">
          Steps to assessing your workplace
        </h2>
        <StepsToAssess />
      </ModuleSection>

      <ModuleSection
        id="effective-consultation"
        sectionTitle="Effective staff consultation"
        sectionNumber={4}
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
          ];
        }, [])}
        className="mb-4"
        ref={section4Ref}
      >
        <div className="row">
          <div className="col-md-10 mx-auto">
            <h2 className="h1 d-block text-warning text-uppercase text-center my-4">
              Effective staff consultation
            </h2>
            <div className="w-75 mx-auto mb-4">
              <img
                className="d-block img-fluid"
                src={ladyAtWork}
                alt="Lady using a tablet at work"
              />
            </div>
            <p>
              Staff consultation not only helps you to understand employee
              wants, needs and barriers, but is also a great engagement tool.
              You may need to manage employee expectations during consultation,
              as the workplace may not be able to deliver on all requests.
              Before you start consulting, there are three decision to make:
            </p>
            <div className="row">
              <div className="col-md-6 d-flex flex-column">
                <strong>Decide how you will consult</strong>
                <p>
                  Think about how you are going to collect results. Here are
                  some methods of consultation you could use:
                </p>
                <ul className="bullet-fulcrum bullet-gold">
                  <li>
                    <p>Online surveys are great for computer-based staff.</p>
                  </li>
                  <li>
                    <p>
                      Paper-based surveys given out to staff or left in high
                      traffic areas.
                    </p>
                  </li>
                  <li>
                    <p>
                      Face-to-face chats or informal discussions with staff
                      members.
                    </p>
                  </li>
                  <li>
                    <p>
                      Focus groups including staff from different departments or
                      locations.
                    </p>
                  </li>
                  <li>
                    <p>
                      Using existing groups or committees as a focus group (e.g.
                      social club).
                    </p>
                  </li>
                  <li>
                    <p>
                      Using natural leaders or enthusiastic staff members within
                      your workplace to help gather feedback.
                    </p>
                  </li>
                </ul>
                <p>
                  Using a number of different collection methods will help you
                  collect the information you need. Offering healthy incentives
                  for staff to participate in consultation might result in
                  better response rates.
                </p>
              </div>
              <div className="col-md-6">
                <strong>Decide who to talk to</strong>
                <p>
                  Determine how many and which staff members you wish to consult
                  with – do you want to speak to your entire workforce, or just
                  a specific group? If you have a large workplace, look at
                  consulting with a high risk group. You could even use this
                  group to test out your strategies.
                </p>
                <strong>Decide what you’re going to ask</strong>
                <p>
                  What is it that you will be asking them? Think about the
                  information you are aiming to gather and ensure your
                  consultation covers the following:
                </p>
                <ul className="bullet-fulcrum bullet-gold">
                  <li>
                    <p>Strategies employees would like to see implemented.</p>
                  </li>
                  <li>
                    <p>
                      Current behaviours, awareness levels and attitudes around
                      behavioural risk factors.
                    </p>
                  </li>
                  <li>
                    <p>
                      Current barriers to undertaking healthy lifestyle
                      behaviours.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="consultation-questions"
        sectionTitle="Example consultation questions"
        sectionNumber={5}
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
          ];
        }, [])}
        className="module4__bg5 section-arrows mb-4"
        fluid
        ref={section5Ref}
      >
        <div className="bg-overlay-blueberry-dark d-flex flex-column text-white px-4 py-5">
          <div className="row">
            <div className="col-md-10 col-lg-8 mx-auto">
              <h2 className="h1 text-uppercase text-center mb-4">
                Example consultation questions
              </h2>
              <p>
                The following provides some example questions that can be used
                for consultation purposes. The questions are grouped by health
                risk factor, and can be used to measure awareness levels,
                attitudes and behaviours. You may like to utilise questions such
                as these to measure baseline levels or track changes that occur
                over time.
              </p>
              <p>
                The list below contains sample questions, but is not exhaustive.
              </p>
            </div>
            <div className="col-md-10 mx-auto">
              <ConsultationQuestions />
            </div>
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="smart-objectives"
        sectionTitle="Creating SMART objectives"
        sectionNumber={6}
        className="mb-4"
        ref={section6Ref}
      >
        <h2 className="h1 text-uppercase text-center text-warning mb-4">
          Creating SMART Objectives
        </h2>
        <SmartObjectives />
      </ModuleSection>

      <ModuleSection
        id="action-plan"
        sectionTitle="Creating a workplace health promotion action plan"
        sectionNumber={7}
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
          ];
        }, [])}
        className="mb-4"
        ref={section7Ref}
      >
        <div className="row mb-4">
          <div className="col-md-10 mx-auto d-flex flex-column text-center">
            <h2 className="h1 text-warning text-uppercase my-4">
              Creating a workplace health promotion action plan
            </h2>
            <p>
              Once you have decided on the priorities for your workplace, an
              action plan will allow you to describe what you want to achieve
              and the steps you will take to do this. You can use your action
              plan to keep track of your program’s progress and successes. Below
              are the essential components of an action plan that will help to
              keep your program on track. Click on each to reveal more
              information:
            </p>
            <ActionPlan />
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="evaluation"
        sectionTitle="Evaluation"
        sectionNumber={8}
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
          ];
        }, [])}
        className="module4__bg8 section-arrows mb-4"
        fluid
        ref={section8Ref}
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5">
          <h2 className="h1 text-uppercase text-center my-4">Evaluation</h2>
          <div className="row mb-4">
            <div className="col-md-10 col-lg-8 mx-auto">
              <div className="embed-responsive embed-responsive-16by9 mb-4">
                <iframe
                  className="embed-responsive-item"
                  src="https://www.youtube.com/embed/v-4g3Y8zhlM"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="video-caption lead text-center">
                Watch as Melanie Pescud from the University of WA takes us
                through the process of evaluating a workplace health and
                wellbeing program and why it's so important to get it right from
                the start.
              </p>
              <div className="row">
                <div className="col-md-6">
                  <p>
                    Evaluation enables you to determine a number of things
                    regarding your program, for example:
                  </p>
                  <ul className="bullet-fulcrum bullet-gold">
                    <li>
                      <p>Have you achieved your objectives?</p>
                    </li>
                    <li>
                      <p>
                        Strengths of your program (i.e. what helped you to
                        succeed?)
                      </p>
                    </li>
                    <li>
                      <p>
                        Barriers to success (i.e. what hindered the achievement
                        of objectives)
                      </p>
                    </li>
                    <li>
                      <p>Improvements that can be made</p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <p>
                    It also allows you to demonstrate success to management,
                    increasing your chances of ongoing support. Evaluation can
                    be simple or detailed, depending on your organisation’s
                    needs. It’s important to plan for evaluation at the
                    beginning, prior to developing and implementing your action
                    plan. This is so you are able to effectively measure whether
                    or not your program has made a difference to the health of
                    your workforce.
                  </p>
                  <p>
                    Now for the good news, once you’ve completed your
                    consultation, you have probably got most of the information
                    needed for your evaluation. If not, you can just add a few
                    questions in addition to your consultation questions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="quiz"
        sectionNumber={9}
        sectionTitle="Quiz"
        className="px-3"
        ref={section9Ref}
      >
        <ModuleQuizForm
          quizHeading={`Answer the following ${QUIZ_4.length} questions to progress to complete this program`}
          quizKey={4}
          data={QUIZ_4}
          onSuccess={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              completedModules: Array.from(
                { length: user.currentModule },
                (v, i) => i + 1,
              ).join(","),
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
          onNextClick={(e) => {
            e.preventDefault();
            scrollToTop();
            finish();
          }}
          onFail={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
          nextText="Complete program"
        />
      </ModuleSection>

      <ModuleFooter
        moduleNumber={4}
        totalSections={9}
        currentSection={state.currentSection}
        sectionTitle={state.currentSectionTitle}
        sectionData={state.currentSectionData || []}
      />
    </ModuleProvider>
  );
};
